import React, { useCallback, useEffect, useState } from 'react';
import client from '../api/client';
import { formatApiDateTime } from '../utils/dateTime';
import { useAuth } from '../contexts/AuthContext';
import './ScheduledRefreshPanel.css';

const TIMEZONE_OPTIONS = [
  { id: 'America/Chicago', label: 'Central (America/Chicago)' },
  { id: 'America/New_York', label: 'Eastern (America/New_York)' },
  { id: 'America/Denver', label: 'Mountain (America/Denver)' },
  { id: 'America/Los_Angeles', label: 'Pacific (America/Los_Angeles)' },
];

function emptyTime() {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function snapshotFromSchedule(s) {
  if (!s) return null;
  return {
    enabled: !!s.enabled,
    timeZone: s.timeZone || 'America/Chicago',
    times: Array.isArray(s.times) && s.times.length ? [...s.times].sort() : ['03:00'],
    lookbackHours: s.lookbackHours ?? 36,
    includeFacilities: s.includeFacilities !== false,
  };
}

function snapshotsEqual(a, b) {
  if (!a || !b) return false;
  return a.enabled === b.enabled
    && a.timeZone === b.timeZone
    && a.lookbackHours === b.lookbackHours
    && a.includeFacilities === b.includeFacilities
    && a.times.length === b.times.length
    && a.times.every((t, i) => t === b.times[i]);
}

function ScheduledRefreshPanel() {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('admin.data-sources', true);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedMsg, setSavedMsg] = useState('');

  const [enabled, setEnabled] = useState(false);
  const [timeZone, setTimeZone] = useState('America/Chicago');
  const [times, setTimes] = useState(['03:00']);
  const [lookbackHours, setLookbackHours] = useState(36);
  const [includeFacilities, setIncludeFacilities] = useState(true);
  const [lastRunAt, setLastRunAt] = useState(null);
  const [nextRunAt, setNextRunAt] = useState(null);
  const [lastRunSummary, setLastRunSummary] = useState(null);
  const [savedSnapshot, setSavedSnapshot] = useState(null);

  const applyScheduleResponse = useCallback((s) => {
    if (!s) return;
    setEnabled(!!s.enabled);
    setTimeZone(s.timeZone || 'America/Chicago');
    setTimes(Array.isArray(s.times) && s.times.length ? [...s.times] : ['03:00']);
    setLookbackHours(s.lookbackHours ?? 36);
    setIncludeFacilities(s.includeFacilities !== false);
    setLastRunAt(s.lastRunAt || null);
    setNextRunAt(s.nextRunAt || null);
    setLastRunSummary(s.lastRunSummary || null);
    setSavedSnapshot(snapshotFromSchedule(s));
  }, []);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setError('');
    try {
      const res = await client.get('/api/cache/refresh-schedule');
      applyScheduleResponse(res.data?.schedule);
    } catch (err) {
      if (!silent) {
        setError(err.response?.data?.error || err.message || 'Failed to load schedule');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [applyScheduleResponse]);

  useEffect(() => { load(); }, [load]);

  // Keep last/next run in sync while a schedule is active.
  useEffect(() => {
    if (!savedSnapshot?.enabled) return undefined;
    const id = setInterval(() => load({ silent: true }), 30000);
    return () => clearInterval(id);
  }, [load, savedSnapshot?.enabled]);

  const buildPayload = (overrides = {}) => ({
    enabled: overrides.enabled ?? enabled,
    timeZone: overrides.timeZone ?? timeZone,
    times: (overrides.times ?? times).filter(Boolean),
    mode: 'smart',
    lookbackHours: overrides.lookbackHours ?? lookbackHours,
    includeFacilities: overrides.includeFacilities ?? includeFacilities,
  });

  const handleSave = async (overrides = {}) => {
    setSaving(true);
    setError('');
    setSavedMsg('');
    try {
      const res = await client.put('/api/cache/refresh-schedule', buildPayload(overrides));
      applyScheduleResponse(res.data?.schedule);
      setSavedMsg('Schedule saved.');
      setTimeout(() => setSavedMsg(''), 5000);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to save schedule');
    } finally {
      setSaving(false);
    }
  };

  const handleEnabledChange = async (checked) => {
    setEnabled(checked);
    if (!canEdit) return;
    await handleSave({ enabled: checked });
  };

  const addTime = () => setTimes(prev => [...prev, emptyTime()]);
  const removeTime = (idx) => setTimes(prev => prev.filter((_, i) => i !== idx));
  const updateTime = (idx, val) => setTimes(prev => prev.map((t, i) => (i === idx ? val : t)));

  const currentSnapshot = {
    enabled,
    timeZone,
    times: [...times].filter(Boolean).sort(),
    lookbackHours,
    includeFacilities,
  };
  const isDirty = savedSnapshot && !snapshotsEqual(currentSnapshot, savedSnapshot);

  if (loading) {
    return <div className="scheduled-refresh-panel info-box">Loading scheduled refresh settings…</div>;
  }

  return (
    <div className="scheduled-refresh-panel">
      <header className="scheduled-refresh-head">
        <div>
          <h3>Scheduled Refresh</h3>
          <p className="scheduled-refresh-desc">
            Automatically pull new VitalEngine data on a daily schedule (smart refresh).
            Runs inside the backend while the app is running — reliable on AWS; on a local
            laptop only fires when Docker is up at the scheduled time. Enable saves
            immediately; other changes require <strong>Save schedule</strong>.
          </p>
        </div>
        <label className="scheduled-refresh-toggle">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => handleEnabledChange(e.target.checked)}
            disabled={!canEdit || saving}
          />
          <span>{enabled ? 'Enabled' : 'Disabled'}</span>
        </label>
      </header>

      {isDirty && (
        <div className="scheduled-refresh-dirty">
          Unsaved changes — click <strong>Save schedule</strong> for run times and other
          settings to take effect.
        </div>
      )}

      <div className="scheduled-refresh-grid">
        <div className="scheduled-refresh-field">
          <label htmlFor="sched-tz">Timezone</label>
          <select
            id="sched-tz"
            value={timeZone}
            onChange={(e) => setTimeZone(e.target.value)}
            disabled={!canEdit}
          >
            {TIMEZONE_OPTIONS.map(o => (
              <option key={o.id} value={o.id}>{o.label}</option>
            ))}
          </select>
        </div>

        <div className="scheduled-refresh-field">
          <label htmlFor="sched-lookback">Lookback (hours)</label>
          <input
            id="sched-lookback"
            type="number"
            min={1}
            max={8760}
            value={lookbackHours}
            onChange={(e) => setLookbackHours(Number(e.target.value) || 36)}
            disabled={!canEdit}
          />
          <span className="scheduled-refresh-hint">
            Used for the first run; nightly smart refresh resumes from the last successful pull.
          </span>
        </div>
      </div>

      <div className="scheduled-refresh-times">
        <div className="scheduled-refresh-times-head">
          <label>Run times</label>
          {canEdit && (
            <button type="button" className="scheduled-refresh-add" onClick={addTime}>
              + Add time
            </button>
          )}
        </div>
        {times.map((t, idx) => (
          <div key={idx} className="scheduled-refresh-time-row">
            <input
              type="time"
              value={t}
              onChange={(e) => updateTime(idx, e.target.value)}
              disabled={!canEdit}
            />
            {canEdit && times.length > 1 && (
              <button
                type="button"
                className="scheduled-refresh-remove"
                onClick={() => removeTime(idx)}
                aria-label="Remove time"
              >
                ×
              </button>
            )}
          </div>
        ))}
      </div>

      <label className="scheduled-refresh-checkbox">
        <input
          type="checkbox"
          checked={includeFacilities}
          onChange={(e) => setIncludeFacilities(e.target.checked)}
          disabled={!canEdit}
        />
        Also refresh Salesforce facilities after each data pull
      </label>
      <p className="scheduled-refresh-hint" style={{ marginTop: '-0.25rem', marginBottom: '0.75rem' }}>
        Uncheck and click <strong>Save schedule</strong> so the nightly job never calls Salesforce.
        Outbound Salesforce traffic can also be hard-paused with env <code>SALESFORCE_SYNC_ENABLED=false</code>
        (manual Sync buttons are blocked too; the last cached facility hierarchy stays in use).
      </p>

      <div className="scheduled-refresh-status">
        <div>
          <span className="scheduled-refresh-status-label">Last run</span>
          <span>{lastRunAt ? formatApiDateTime(lastRunAt) : '—'}</span>
        </div>
        <div>
          <span className="scheduled-refresh-status-label">Next run</span>
          <span>
            {!savedSnapshot?.enabled ? 'Off'
              : nextRunAt ? formatApiDateTime(nextRunAt)
                : enabled ? 'Pending save…' : '—'}
          </span>
        </div>
        {lastRunSummary && (
          <div className="scheduled-refresh-summary">
            <span className="scheduled-refresh-status-label">Last result</span>
            <span>
              {lastRunSummary.success === false ? 'Errors — ' : 'OK — '}
              {lastRunSummary.recordsAdded != null && `${lastRunSummary.recordsAdded} records`}
              {lastRunSummary.facilitiesCount != null && ` · ${lastRunSummary.facilitiesCount} facilities`}
              {Array.isArray(lastRunSummary.failedTypes) && lastRunSummary.failedTypes.length > 0
                && ` · failed: ${lastRunSummary.failedTypes.join(', ')}`}
            </span>
          </div>
        )}
      </div>

      {error && <div className="scheduled-refresh-error">{error}</div>}
      {savedMsg && <div className="scheduled-refresh-saved">{savedMsg}</div>}

      {canEdit && (
        <div className="scheduled-refresh-actions">
          <button
            type="button"
            className="refresh-btn primary"
            onClick={() => handleSave()}
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save schedule'}
          </button>
        </div>
      )}
    </div>
  );
}

export default ScheduledRefreshPanel;
